'use client'
import { motion, useScroll, useTransform, useSpring } from 'framer-motion'
import Image from 'next/image'
import Link from 'next/link'
import { useState } from 'react'
import { usePathname, useRouter } from 'next/navigation'
import { handleSignUpClick } from '@/lib/tracking'
import { scrollToSection } from '@/lib/scroll'

const Navbar = () => {
	const [isOpen, setIsOpen] = useState(false)
	const pathname = usePathname()
	const router = useRouter()
	const isHomePage = pathname === '/'
	const { scrollYProgress } = useScroll()
	const navScale = useSpring(useTransform(scrollYProgress, [0, 0.1], [1, 0.95]), {
		damping: 20,
		stiffness: 100,
	})
	const logoSrc = "/logo_dark.svg"

	const LINKS = [
		{ name: 'Home', link: '#hero' },
		{ name: 'Features', link: '#features' },
		{ name: 'Pricing', link: '#pricing' },
		{ name: 'About', link: '/about' },
		{ name: 'Support', link: '#contact' },
	]

	const handleScroll = (e: any, target: any) => {
		e.preventDefault()
		if (isHomePage) {
			scrollToSection(target)
		} else {
			// Navigate to home page with hash, scroll after navigation
			router.push('/' + target)
		}
	}

	return (
		<motion.div id="navbar-container" className="fixed w-full z-[900] px-4 pt-4" style={{ scale: navScale, top: 'calc(var(--banner-height, 0px))' }}>
			<motion.nav
				initial={{ y: -100, opacity: 0 }}
				animate={{ y: 0, opacity: 1 }}
				className="max-w-5xl mx-auto rounded-full bg-white shadow-xl backdrop-blur-lg py-3 pl-6 pr-3 max-lg:pr-6 flex items-center justify-between"
			>
				{/* Logo */}
				<div className="flex items-center w-1/3">
					<Image
						src={logoSrc}
						alt="Logo"
						width={140}
						height={32}
						className="h-auto w-[178px] cursor-pointer"
						priority
						onClick={() => isHomePage ? window.scrollTo({ top: 0, behavior: 'smooth' }) : router.push('/')}
					/>
				</div>

				{/* Desktop Links - Middle */}
				<div className="hidden lg:flex justify-center w-1/3">
					<div className="flex items-center space-x-6">
						{LINKS.map(({ name, link }) => (
							link.startsWith('#') ? (
								<a
									key={name}
									href={link}
									onClick={(e) => handleScroll(e, link)}
									className="relative group text-gray-700 hover:text-[#3BCF8E] transition-colors font-semibold"
								>
									{name}
								</a>
							) : (
								<Link
									key={name}
									href={link}
									className="relative group text-gray-700 hover:text-[#3BCF8E] transition-colors font-semibold"
								>
									{name}
								</Link>
							)
						))}
					</div>
				</div>

				{/* Desktop Button - Right */}
				<div className="hidden lg:flex justify-end w-1/3">
					<Link href={'https://app.infinitcode.ai/'} target='_blank'>
						<motion.button
							whileHover={{ scale: 1.05 }}
							className="bg-gradient-to-r from-[#3BCF8E] to-[#2DA771] text-white px-6 py-2 rounded-full"
							onClick={handleSignUpClick}
						>
							Get a free trial
						</motion.button>
					</Link>
				</div>

				{/* Mobile Menu Button */}
				<div className="lg:hidden flex items-center">
					<label className="hamburger">
						<input type="checkbox" checked={isOpen} onChange={() => setIsOpen(!isOpen)} aria-label="Toggle mobile menu" />
						<svg viewBox="0 0 28 28">
							<path
								className="line line-top-bottom"
								d="M27 10 13 10C10.8 10 9 8.2 9 6 9 3.5 10.8 2 13 2 15.2 2 17 3.8 17 6L17 26C17 28.2 18.8 30 21 30 23.2 30 25 28.2 25 26 25 23.8 23.2 22 21 22L7 22"
								style={{
									strokeDasharray: isOpen ? '20 300' : '12 63',
									strokeDashoffset: isOpen ? '-32.42' : '0',
									stroke: '#020F2A',
									transition:
										'stroke-dasharray 600ms cubic-bezier(0.4, 0, 0.2, 1), stroke-dashoffset 600ms cubic-bezier(0.4, 0, 0.2, 1)',
								}}
							></path>
							<path
								className="line"
								d="M7 16 27 16"
								style={{
									stroke: '#020F2A',
									transition: 'stroke 300ms ease-in-out',
								}}
							></path>
						</svg>
					</label>
				</div>
			</motion.nav>

			{/* Mobile Dropdown */}
			{isOpen && (
				<motion.div
					initial={{ opacity: 0, y: -20 }}
					animate={{ opacity: 1, y: 0 }}
					className="lg:hidden absolute top-20 left-0 right-0 bg-white/95 backdrop-blur-lg mx-4 rounded-xl shadow-xl"
				>
					<div className="flex flex-col p-4 space-y-4">
						{LINKS.map(({ name, link }) => (
							link.startsWith('#') ? (
								<a
									key={name}
									href={link}
									onClick={(e) => {
										handleScroll(e, link)
										setIsOpen(false)
									}}
									className="text-gray-700 hover:text-[#3BCF8E] px-4 py-2"
								>
									{name}
								</a>
							) : (
								<Link
									key={name}
									href={link}
									onClick={() => setIsOpen(false)}
									className="text-gray-700 hover:text-[#3BCF8E] px-4 py-2"
								>
									{name}
								</Link>
							)
						))}
						<Link href={'https://app.infinitcode.ai/'} target='_blank' className='w-full'>
							<button
								className="bg-gradient-to-r from-[#3BCF8E] to-[#2DA771] text-white px-6 py-3 rounded-lg mt-4 w-full"
								onClick={handleSignUpClick}
							>
								Get a free trial
							</button>
						</Link>
					</div>
				</motion.div>
			)}
		</motion.div>
	)
}

export default Navbar;